/**
 * Organization Tools
 *
 * Trial tools an admin switches on for one organization at a time
 * (`Organization.enabledTools`). They are off by default: a tool listed here
 * is reachable only for organizations whose stored list names it.
 *
 * This module is browser-safe on purpose: the admin "Tools" dialog, the plan
 * endpoint and the API gate all read the same allowlist and labels, so the
 * three can't drift. Keep it free of database and SDK imports.
 *
 * Modeled on `add-on-platforms.ts`.
 */

/** Tools an admin can enable for an organization. */
export const ORGANIZATION_TOOLS = ["youtube_optimizer"] as const;

export type OrganizationTool = (typeof ORGANIZATION_TOOLS)[number];

/** Display labels for the admin UI. */
export const ORGANIZATION_TOOL_LABELS: Record<OrganizationTool, string> = {
	youtube_optimizer: "YouTube Optimizer",
};

/** One-line descriptions for the admin UI. */
export const ORGANIZATION_TOOL_DESCRIPTIONS: Record<OrganizationTool, string> =
	{
		youtube_optimizer:
			"Turns what a video says into an AI-citable YouTube title, description and captions.",
	};

export function isOrganizationTool(value: string): value is OrganizationTool {
	return (ORGANIZATION_TOOLS as readonly string[]).includes(value);
}

/**
 * Read an organization's stored tool list into a validated, deduped,
 * canonically ordered set.
 *
 * Unknown values are dropped rather than thrown on: the column is a plain
 * String[] an admin wrote, and one stale entry must never lock an
 * organization out of the tools it does have.
 */
export function resolveOrganizationTools(
	stored: readonly string[] | null | undefined,
): OrganizationTool[] {
	if (!stored?.length) return [];
	const enabled = new Set(stored.filter(isOrganizationTool));
	return ORGANIZATION_TOOLS.filter((tool) => enabled.has(tool));
}

/** Whether `tool` is enabled in an organization's stored list. */
export function hasOrganizationTool(
	stored: readonly string[] | null | undefined,
	tool: OrganizationTool,
): boolean {
	return resolveOrganizationTools(stored).includes(tool);
}
